// SPDX-License-Identifier: AGPL-3.0-or-later

import {GuildSettingsModal} from '@app/features/app/components/dialogs/LoadableSettingsModals';
import styles from '@app/features/guild/components/GuildPulseProgressBar.module.css';
import type {Guild} from '@app/features/guild/models/Guild';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import {LightningIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import React, {useCallback, useMemo} from 'react';

export const GuildPulseProgressBar: React.FC<{guild: Guild}> = observer(({guild}) => {
	const count = guild.premiumSubscriptionCount ?? 0;

	const {levelTitle, countText, progressPercent, tooltip} = useMemo(() => {
		if (count < 2) {
			const pct = Math.min(100, Math.max(0, (count / 2) * 100));
			return {
				levelTitle: count === 0 ? 'ND Pulse' : 'Level 0',
				countText: `${count} / 2 Pulses`,
				progressPercent: pct,
				tooltip: `${count} of 2 pulses to reach Level 1`,
			};
		}
		if (count < 7) {
			const pct = Math.min(100, Math.max(0, ((count - 2) / (7 - 2)) * 100));
			return {
				levelTitle: 'Level 1',
				countText: `${count} / 7 Pulses`,
				progressPercent: pct,
				tooltip: `${count} of 7 pulses to reach Level 2`,
			};
		}
		if (count < 14) {
			const pct = Math.min(100, Math.max(0, ((count - 7) / (14 - 7)) * 100));
			return {
				levelTitle: 'Level 2',
				countText: `${count} / 14 Pulses`,
				progressPercent: pct,
				tooltip: `${count} of 14 pulses to reach Level 3`,
			};
		}
		return {
			levelTitle: 'Level 3',
			countText: `${count} Pulses (Max)`,
			progressPercent: 100,
			tooltip: `Server is at maximum Level 3 with ${count} pulses!`,
		};
	}, [count]);

	const handleClick = useCallback(() => {
		ModalCommands.push(
			modal(
				() => (
					<GuildSettingsModal
						guildId={guild.id}
						initialTab="pulse"
						data-flx="guild.boost-progress-bar.guild-settings-modal-pulse"
					/>
				),
				'guild-settings',
			),
		);
	}, [guild.id]);

	return (
		<div
			className={styles.container}
			onClick={handleClick}
			role="button"
			tabIndex={0}
			title={tooltip}
			aria-label={`ND Pulse status: ${levelTitle}, ${countText}`}
			data-flx="guild.pulse-progress-bar.container"
		>
			<div className={styles.headerRow} data-flx="guild.pulse-progress-bar.header-row">
				<div className={styles.levelBadge} data-flx="guild.pulse-progress-bar.level-badge">
					<LightningIcon weight="fill" size={14} className={styles.pulseIcon} />
					<span>{levelTitle}</span>
				</div>
				<span className={styles.countLabel} data-flx="guild.pulse-progress-bar.count-label">
					{countText}
				</span>
			</div>
			<div className={styles.progressTrack} data-flx="guild.pulse-progress-bar.progress-track">
				<div
					className={styles.progressBar}
					style={{width: `${Math.max(4, progressPercent)}%`}}
					data-flx="guild.pulse-progress-bar.progress-bar"
				/>
			</div>
		</div>
	);
});
